// **The deck's brand palette and typeface — stated exactly once** (presentation polish, 2026-08-11).
//
// The `/demo` slide deck is reskinned to the company brand; the *game* keeps its Field Journal
// look. So none of this is a kit token, and none of it may reach the app's own chrome.
//
// **Why a `.ts` module and not (only) custom properties in `slides.css`,** which is what the polish
// spec's letter asks for. Two reasons, both about stating a value once:
//
//  1. `slides.test.tsx`'s `findHardCodedKitTokenValues` scan reads every `.tsx` file in this
//     directory and fails on any hex literal that duplicates a kit token *value*. The brand's
//     near-white card fill is `#FFFDF6`, which *is* a kit value (`--hud-bg`, `--surface-card`), so a
//     literal in a component would fail that scan — correctly, by its own rule.
//  2. Mermaid's `themeVariables` is a JavaScript object and cannot read a CSS custom property, so a
//     stylesheet-only palette would have to be written out a second time for the diagrams.
//
// `SlideFrame` publishes these as `--deck-*` custom properties **inline on its own root element**,
// so they cascade to the deck's subtree and cannot leak into the game's chrome; `slides.css` and
// every slide component reference `var(--deck-*)`, and the diagram theme reads this module directly.
import { SLIDE_SCALE } from './scale';

export const DECK = {
  /** The deck ground. Flat — the honey radial wash is gone (Mermaid cannot draw one either). */
  bg: '#FFFFFF',
  /** Body and hero text. */
  ink: '#000000',
  /** Captions, glosses, sublabels. */
  caption: '#3B3B3B',
  /** Secondary/emphasis: the brand red. Strokes, emphasised words, accent marks. */
  accent: '#A240FE',
  /** Secondary Festival emphasis: Lava. */
  secondary: '#A240FE',
  /** Card fill. */
  card: '#FFFFFF',
  /** The accent at ~40% over `bg` — a card hairline that still reads from the back of a room
   * without becoming a second accent. */
  cardBorder: '#A240FE',
  /** The accent at ~14% over `bg` — the "hot" card's fill, the recoloured survivor of the kit's
   * `--accent-soft`. */
  hot: '#A240FE',
  /** The near-black Festival brand field used by the opening and finisher circles. */
  circle: '#221F20',
  /** The DEATHMATCH slide's ground. */
  deathmatch: 'rgb(188, 18, 14)',
  /** Pink Lava, the light endpoint for hot gradients and the finisher impact line. */
  pinkLava: '#FF91FF',
  /** Pink Lava at half intensity — exactly halfway between `secondary`/`hot` (#A240FE) and
   * `pinkLava`. The gradient endpoint every hot surface fades *to*; the full Pink Lava stays for
   * flat uses like the finisher impact line. */
  pinkLavaHalf: '#D169FF',
  /** Ocean, the dark endpoint for code/computer diagram nodes. */
  ocean: '#51ADE2',
  oceanLight: '#9BD8F3',
  /** Ocean's light endpoint at half intensity — exactly halfway between `ocean` and `oceanLight`,
   * the same toning-down `pinkLavaHalf` is for the hot nodes. What every ocean gradient fades to. */
  oceanLightHalf: '#76C3EB',
  /** Replaces `--font-display` *and* `--font-body` inside the deck: the brand has one typeface, so
   * the deck's display/body distinction is carried by weight (600 / 500), not by family. */
  font: "'Figtree', ui-sans-serif, system-ui, -apple-system, sans-serif",
} as const;

/** The `--deck-*` custom properties `SlideFrame` puts on its root element, plus the two kit font
 * variables it shadows there. Exported as data so the frame declares no palette of its own. */
export const DECK_CSS_VARS = {
  '--deck-bg': DECK.bg,
  '--deck-ink': DECK.ink,
  '--deck-caption': DECK.caption,
  '--deck-accent': DECK.accent,
  '--deck-secondary': DECK.secondary,
  '--deck-card': DECK.card,
  '--deck-card-border': DECK.cardBorder,
  '--deck-hot': DECK.hot,
  '--deck-circle': DECK.circle,
  '--deck-pink-lava': DECK.pinkLava,
  '--deck-pink-lava-half': DECK.pinkLavaHalf,
  '--deck-deathmatch': DECK.deathmatch,
  '--deck-ocean': DECK.ocean,
  '--deck-ocean-light': DECK.oceanLight,
  '--deck-ocean-light-half': DECK.oceanLightHalf,
  '--font-display': DECK.font,
  '--font-body': DECK.font,
} as const;

/** Mermaid `themeVariables` for the deck: Figtree labels, brand-red strokes, near-white node
 * fills, black text. `theme: 'base'` is the only theme whose variables are actually honoured. */
export const DECK_MERMAID_THEME = {
  fontFamily: DECK.font,
  // The deck's own node size, not a second one: `SLIDE_SCALE.node` is what the hand-built diagram
  // nodes were set at and what the surviving chips are still set at.
  fontSize: `${SLIDE_SCALE.node}px`,
  background: DECK.bg,
  primaryColor: DECK.card,
  primaryBorderColor: DECK.secondary,
  primaryTextColor: DECK.ink,
  secondaryColor: DECK.hot,
  tertiaryColor: DECK.card,
  lineColor: DECK.secondary,
  textColor: DECK.ink,
  edgeLabelBackground: DECK.bg,
  nodeBorder: DECK.secondary,
  mainBkg: DECK.card,
  clusterBkg: DECK.bg,
  titleColor: DECK.ink,
} as const;

/** The one `classDef` every deck diagram shares, so the "hot" node in a branch (German, the LLM,
 * a hug) keeps the emphasis the hand-built diagrams gave it. */
// No `stroke`: a filled node carries its own colour, and a brand-red hairline around it read as a
// second border when projected (2026-08-13). `decorateDeckDiagram` sets `stroke: none` inline on
// these shapes, so this is the source agreeing with what is drawn rather than the thing that draws
// it. Unfilled nodes ("My brain", "English") keep their border from the theme's `nodeBorder`.
export const DECK_MERMAID_HOT_CLASSDEF = `  classDef hot fill:${DECK.hot},color:${DECK.card}`;
export const DECK_MERMAID_MACHINE_CLASSDEF = `  classDef machine fill:${DECK.ocean},color:${DECK.card}`;
